import { handleCurrentSelfCheckStep, handleCurrentStep, handleCurrentTSTCStep } from "../redux/slices/userInfoStepperSlice";
import { toast } from "react-toastify";
import moment from "moment/moment";

import { BankNameDropList, durationType } from "../data/Constants";
import { UserCPDStatus } from "../data/cpdActivityTypeConstants";
import { paymentService } from "../services/paymentService";
import { PaymentStatus } from "../data/paymentConstants";

export const handleBack = ({ dispatch, step }) => {
    dispatch(handleCurrentStep(step));
};

export const handleSelfCheckBack = async ({ dispatch, step }) => {
    dispatch(handleCurrentSelfCheckStep(step));
};

export const handleTSTCBack = async ({ dispatch, step }) => {
    dispatch(handleCurrentTSTCStep(step));
};

export function generateLabelsAndValues(constantsObject) {
    return Object.keys(constantsObject).map((key) => ({
        label: key
            .split("_")
            .map((word) => (word === "ATP" ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
            .join(" "),
        value: constantsObject[key],
    }));
}

const prefixes = ["A", "B", "C", "D", "E", "F", "G", "H", "J", "K", "L", "M", "N", "P", "Q", "S", "T", "U", "W", "Y", "Z"];

export function generateSchemaJpaGovernmentLabelsAndValues(constantsObject) {
    return Object.keys(constantsObject).map((key, index) => ({
        label:
            `${prefixes[index]} – ` +
            key
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(" "),
        value: constantsObject[key],
    }));
}

const prefixesForTypeOfCPD = ["A1.1", "A1.2", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A8.1", "A8.2", "A8.3", "B1", "B2", "B3", "D1", "D2", "D3"];

export function generateTypeOfCPDLabelsAndValues(constantsObject) {
    return Object.keys(constantsObject).map((key, index) => ({
        label:
            `${prefixesForTypeOfCPD[index]} – ` +
            key
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(" "),
        value: constantsObject[key],
    }));
}

export function generateLabelAndValuesVariant({ enums, labels }) {
    const labelValuePairs = [];

    for (const key in enums) {
        const enumValue = enums[key];
        const label = labels[enumValue - 1];
        labelValuePairs.push({ label, value: enumValue });
    }

    return labelValuePairs;
}

const updatePaymentStatus = async ({ data, failed, handlePaymentComplete, setIsNextBtnLoading }) => {
    if (setIsNextBtnLoading) {
        setIsNextBtnLoading(true)
    }
    try {
        if (failed) {
            handlePaymentComplete(false);
        } else {
            handlePaymentComplete(true);
        }
        const response = await paymentService.updatePaymentStatus({ data }); // Call the service with data
        return response?.data?.data; // Return the relevant part of the response
    } catch (error) {
        handlePaymentComplete(false);
        console.error(error); // Log any errors
    } finally {
        if (setIsNextBtnLoading) {
            setIsNextBtnLoading(false)
        }
    }
};

export const CalcYears = () => {
    const startYear = 1990;
    const endYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
        years.push({ value: year, label: year });
    }
    return years;
};

export const YearOptions = CalcYears();

export const CalcHours = (totalHours) => {
    const hours = [];
    for (let hour = 0; hour <= totalHours; hour++) {
        hours.push({ value: hour, label: hour });
    }
    return hours;
};

// Example: Generating options for 24 hours
export const HourOptions = CalcHours(24);

export const calculateDaysDifference = (pendingDays) => {
    if (!pendingDays) return null;

    const currentDate = new Date();
    const pendingDate = new Date(pendingDays);

    // Calculate the difference in milliseconds
    const differenceInTime = pendingDate.getTime() - currentDate.getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

    // If pendingDays is today, return 1 day instead of 0
    if (differenceInDays === 0) {
        return 1;
    }

    return differenceInDays;
};

export const handleDownloadFile = (url, name) => {
    if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
};

export const openPdfBlob = (blob, filename = "certificate.pdf") => {
    const blobUrl = URL.createObjectURL(
        blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" }),
    );
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

export const getBlobErrorMessage = async (error) => {
    const data = error?.response?.data;
    if (data instanceof Blob && data.type?.includes("application/json")) {
        try {
            const json = JSON.parse(await data.text());
            return json?.message || "Download failed.";
        } catch {
            return "Download failed.";
        }
    }
    return error?.response?.data?.message || error?.message || "Download failed.";
};

export const downloadAuthenticatedFile = async (url, filename = "certificate.pdf") => {
    if (!url) {
        throw new Error("File URL is missing");
    }

    const token = localStorage.getItem("token");
    const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
        throw new Error("Download failed");
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
};

// Helper function to get duration type string
export const getDurationTypeString = (type) => {
    switch (type) {
        case durationType.HOURS:
            return "hours";
        case durationType.DAYS:
            return "days";
        default:
            return ""; // You can handle other cases here if needed
    }
};

// Helper function to get status name
export const getStatusName = (status) => {
    switch (status) {
        case UserCPDStatus.PENDING:
            return "Submit Now";
        case UserCPDStatus.SUBMITTED:
            return "Submitted";
        case UserCPDStatus.APPROVED:
            return "Approved";
        case UserCPDStatus.REJECTED:
            return "Rejected";
        case UserCPDStatus.KIV:
            return "KIV";
        case UserCPDStatus.INCOMPLETE_DOCUMENT:
            return "Incomplete Document";
        default:
            return ""; // You can handle other cases here if needed
    }
};

// loads the cdn
export function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.setAttribute("data-cancel", "cancelCallback");
        script.setAttribute("data-error", "errorCallback");
        script.setAttribute("data-complete", "completeCallback");
        script.setAttribute("data-timeout", "timeoutCallback");

        document.head.appendChild(script);
    });
}

// this function is called for certificate 1 stepper, certificate 2 stepper and interview
export const handlePay = async ({ checkoutSessionType, setError, setPaymentPopup, handleLoading, handlePaymentComplete, certificateApplicationId, dispatch, step, setIsNextBtnLoading }) => {
    try {
        handleLoading(true);
        // Fetch session ID
        const sessionResponse = await paymentService.getSessionID({
            data: { type: checkoutSessionType },
        });

        // Check payment status from sessionResponse.data (assuming payment_status is a property of sessionResponse.data)
        const currentPaymentStatus = sessionResponse?.data?.payment_status;

        // If payment status is already SUCCESS, mark payment as successful
        if (currentPaymentStatus === 2) {
            // Optionally handle success UI or navigation logic here
            handleBack({ dispatch, step: step });
            // return;
        }

        // load the payment gateway
        await loadScript(process.env.REACT_APP_PAYMENT_GATEWAY);
        if (window.Checkout) {
            // callbacks for various status
            window.errorCallback = async (e) => {
                const data = {
                    type: checkoutSessionType,
                    payment_status: PaymentStatus.FAILED,
                    result_indicator: e.resultIndicator,
                    certificate_application_id: Number(certificateApplicationId),
                    date: moment().format()
                };
                const failed = true;
                const response = await updatePaymentStatus({
                    data,
                    failed,
                    handlePaymentComplete,
                    setIsNextBtnLoading
                });
                setError(e);
            };

            window.completeCallback = async (e) => {
                const data = {
                    type: checkoutSessionType,
                    payment_status: PaymentStatus.SUCCESS,
                    result_indicator: e.resultIndicator,
                    certificate_application_id: Number(certificateApplicationId),
                    date: moment().format()
                };
                const failed = false;
                const response = await updatePaymentStatus({
                    data,
                    failed,
                    handlePaymentComplete,
                    setIsNextBtnLoading
                });
            };

            window.timeoutCallback = (e) => {
                setError(e);
            };
            window.cancelCallback = async (e) => {
                const data = {
                    type: checkoutSessionType,
                    payment_status: PaymentStatus.CANCELLED,
                    result_indicator: e.resultIndicator,
                    certificate_application_id: Number(certificateApplicationId),
                    date: moment().format()
                };
                const failed = true;
                const response = await updatePaymentStatus({
                    data,
                    failed,
                    handlePaymentComplete,
                    setIsNextBtnLoading
                });
                setError(e);
            };
            handleLoading(true);
            // Once we have the "Checkout" obj from the loaded script, call the api to get session id
            try {
                const response = await paymentService.getSessionID({
                    data: { type: checkoutSessionType }, // Pass the dynamic type here
                });
                window.Checkout.configure({
                    session: {
                        id: response?.data?.id,
                    },
                });
                handleLoading(false);
                setPaymentPopup(true);
            } catch (configError) {
                toast.error("Please try again.");
            }
        } else {
            toast.error("Please try again.");
        }
    } catch (scriptError) {
        toast.error(scriptError?.message);
    } finally {
        handleLoading(false);
    }
};

export const formatDate = (date, format) => {
    if (date === null || date === undefined || date === "") {
        return "";
    }
    const m = moment(date);
    return m.isValid() ? m.format(format) : "";
};

/** Display helper: parses UTC instants from API, shows in the browser's local timezone. */
export function formatDateForDisplay(value, format = "DD/MM/YYYY", fallback = "-") {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }
    const m = moment.utc(value);
    if (!m.isValid()) {
        return fallback;
    }
    return m.local().format(format);
}

export function generateCivilAviationLabelsAndValues(constantsObject) {
    return Object.entries(constantsObject).map(([key, value]) => ({
        label: value,
        value: key,
    }));
}

// function to calculate the date
export function calculateWorkingDays(startDate, numberOfDays) {
    const date = new Date(startDate);
    let count = 0;

    while (count < numberOfDays) {
        date.setDate(date.getDate() + 1);

        // Check if the current day is a weekday (Monday to Friday)
        if (date.getDay() !== 0 && date.getDay() !== 6) {
            count++;
        }
    }

    return date;
}

export function generateLabelsAndValuesForAcademic(constantsObject) {
    const labelMapping = {
        ENGINEERING: "Engineering/ Engineering Technology",
        TECHNOLOGY_AND_TECHNICAL: "Technology and Technical",
        SCIENCE_APPLIED_SCIENCE: "Science/ Applied Science",
        NON_TECHNICAL: "Non-technical",
    };

    return Object.keys(constantsObject).map((key) => ({
        label: labelMapping[key],
        value: constantsObject[key],
    }));
}